// BabyAI client — "Bundle of Babies." Christopher's MoE of small open
// models on a private HuggingFace Space (novasynchris-babyai.hf.space),
// OpenAI-compatible, browser-direct. Streaming single-model path only —
// BKA's composer drives the document through the prompt-defined
// <edit>/<create_document> tags (lib/doc-edit-stream.ts), which any
// instruct model can emit, so we don't need native tool-calls.
//
// Trimmed from foundry's babyai-client.ts: just the streaming call (no
// Mosh Pit / feedback / MoE telemetry). The Space is PRIVATE, so the
// HF token IS the access credential. Default token is baked at build
// time (VITE_BABYAI_HF_TOKEN) so chat works with zero setup; a creator
// can override with their own in the key modal.

import { readOpenAIStream } from "./openai-sse";

const ENDPOINT = "https://novasynchris-babyai.hf.space/v1/chat/completions";

export interface BabyAIModelChoice {
  id: string;
  label: string;
  blurb: string;
}

export const BABYAI_MODELS: BabyAIModelChoice[] = [
  { id: "hf_llama_8b", label: "Llama 3.1 8B", blurb: "general chat — the default" },
  { id: "hf_glm_4_7_flash", label: "GLM-4.7-Flash", blurb: "reasoning, sharper prose" },
  { id: "hf_qwen3_coder_30b", label: "Qwen3 Coder 30B", blurb: "best for code + structure" },
];

export const DEFAULT_BABYAI_MODEL = "hf_llama_8b";

/** The build-time default HF token. Public by necessity (private Space,
 *  no backend to hide it) — see the security note in the Sprint 4 commit
 *  / README. A creator-pasted token overrides this. */
export function defaultBabyaiToken(): string | null {
  const t = import.meta.env.VITE_BABYAI_HF_TOKEN as string | undefined;
  return t && t.length > 0 ? t : null;
}

export interface BabyAIMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface BabyAIStreamOptions {
  token: string;
  model: string;
  messages: BabyAIMessage[];
  systemInstruction?: string;
  maxTokens?: number;
  temperature?: number;
  onToken: (delta: string) => void;
}

export interface BabyAIResult {
  content: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export async function callBabyAIStream(opts: BabyAIStreamOptions): Promise<BabyAIResult> {
  const messages: BabyAIMessage[] = opts.systemInstruction
    ? [{ role: "system", content: opts.systemInstruction }, ...opts.messages]
    : opts.messages;

  const body = JSON.stringify({
    model: opts.model,
    messages,
    temperature: opts.temperature ?? 0.7,
    max_tokens: opts.maxTokens ?? 2048,
    stream: true,
  });

  // One-shot 5xx/network retry — the Space cold-starts from sleep and
  // the first hit after idle can 503.
  let resp: Response | null = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      resp = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `Bearer ${opts.token}`,
        },
        body,
      });
    } catch (e) {
      if (attempt === 0) { await sleep(1200); continue; }
      throw new Error(`Bundle of Babies fetch failed (network/CORS): ${e instanceof Error ? e.message : String(e)}`);
    }
    if (resp.status >= 500 && resp.status < 600 && attempt === 0) {
      await sleep(1500); // likely a cold Space — give it a beat
      continue;
    }
    break;
  }
  if (!resp) throw new Error("Bundle of Babies: no response");
  if (!resp.ok) {
    let detail = "";
    try { const t = await resp.text(); detail = t.slice(0, 240); } catch { /* ignore */ }
    throw new Error(`Bundle of Babies ${resp.status} ${resp.statusText}${detail ? ` — ${detail}` : ""}`);
  }

  const result = await readOpenAIStream(resp, opts.onToken);
  return {
    content: result.text,
    model: result.model ?? opts.model,
    promptTokens: result.usage?.prompt_tokens ?? 0,
    completionTokens: result.usage?.completion_tokens ?? 0,
  };
}
